import React, { useEffect, useState } from "react";
import { api, euro, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Gift, Loader2, ArrowLeft, CreditCard } from "lucide-react";
import { useNavigate } from "react-router-dom";

const eur = (n) => euro(n);

export default function GiftCardCliente() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [sel, setSel] = useState(null);
  const [amount, setAmount] = useState("");
  const [email, setEmail] = useState("");
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    api.get("/reloadly/giftcards/customer-catalog", { params: { country: "IT" } })
      .then((r) => setProducts(r.data.products || []))
      .catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || "Catalogo non disponibile"))
      .finally(() => setLoading(false));
  }, []);

  const openBuy = (p) => {
    setSel(p);
    const fixed = p.fixedRecipientDenominations || [];
    setAmount(fixed.length ? String(fixed[0]) : String(p.minRecipientDenomination || ""));
    setEmail("");
  };

  const pay = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error("Scegli un importo valido"); return; }
    if (!email.includes("@")) { toast.error("Inserisci un'email valida"); return; }
    setPaying(true);
    try {
      const { data } = await api.post("/reloadly/giftcards/customer-checkout", {
        productId: sel.productId, brandName: sel.brandName, amount: amt, recipientEmail: email,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore nel pagamento");
      setPaying(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="giftcard-cliente-page">
      <button onClick={() => navigate("/app/servizi")} className="app-text-button flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="giftcard-back"><ArrowLeft className="w-4 h-4" /> Servizi</button>
      <div>
        <p className="text-xs font-medium tracking-wider uppercase text-muted-foreground">Gift Card</p>
        <h1 className="font-head text-3xl font-bold tracking-tight mt-1 flex items-center gap-2"><Gift className="w-7 h-7 text-fuchsia-500" /> Buoni regalo</h1>
        <p className="text-sm text-muted-foreground mt-1">Scegli un brand, l'importo e paga con carta. Il codice arriva subito via email.</p>
      </div>

      {loading && <div className="flex items-center gap-2 text-muted-foreground" data-testid="giftcard-loading"><Loader2 className="w-4 h-4 animate-spin" /> Carico il catalogo…</div>}
      {err && <p className="text-sm text-red-400" data-testid="giftcard-error">{err}</p>}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="giftcard-grid">
        {products.map((p) => (
          <Card key={p.productId} className="p-4 flex flex-col gap-3 glow-border hover:border-primary/60 transition-colors" data-testid={`giftcard-item-${p.productId}`}>
            <div className="flex items-center gap-3">
              {p.logoUrl ? <img src={p.logoUrl} alt={p.brandName} className="w-12 h-12 rounded-lg object-contain bg-white/5 p-1" /> : <div className="w-12 h-12 rounded-lg bg-fuchsia-500/10 flex items-center justify-center"><Gift className="w-6 h-6 text-fuchsia-500" /></div>}
              <div className="min-w-0"><p className="font-semibold truncate">{p.brandName}</p><p className="text-xs text-muted-foreground">{p.currency}</p></div>
            </div>
            <p className="text-xs text-muted-foreground">
              {(p.fixedRecipientDenominations || []).length
                ? `Tagli: ${p.fixedRecipientDenominations.slice(0, 6).map((d) => eur(d)).join(", ")}`
                : `Da ${eur(p.minRecipientDenomination)} a ${eur(p.maxRecipientDenomination)}`}
            </p>
            <Button className="mt-auto gap-1" onClick={() => openBuy(p)} data-testid={`giftcard-buy-${p.productId}`}><Gift className="w-4 h-4" /> Acquista</Button>
          </Card>
        ))}
      </div>
      {!loading && products.length === 0 && !err && <p className="text-muted-foreground">Nessuna gift card disponibile al momento.</p>}

      <Dialog open={!!sel} onOpenChange={(v) => !v && setSel(null)}>
        <DialogContent data-testid="giftcard-buy-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Gift className="w-5 h-5 text-fuchsia-500" /> {sel?.brandName}</DialogTitle>
            <DialogDescription>Scegli l'importo e l'email a cui inviare il buono. Pagamento sicuro con carta.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Importo</Label>
              {(sel?.fixedRecipientDenominations || []).length ? (
                <div className="flex flex-wrap gap-2 mt-2" data-testid="giftcard-denoms">
                  {sel.fixedRecipientDenominations.map((d) => (
                    <Button key={d} type="button" size="sm" className="sim-chip" variant={String(d) === amount ? "default" : "outline"} onClick={() => setAmount(String(d))} data-testid={`giftcard-denom-${d}`}>{eur(d)}</Button>
                  ))}
                </div>
              ) : (
                <Input type="number" min={sel?.minRecipientDenomination} max={sel?.maxRecipientDenomination} value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1.5" data-testid="giftcard-amount" />
              )}
            </div>
            <div>
              <Label>Email destinatario</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="nome@email.it" className="mt-1.5" data-testid="giftcard-email" />
            </div>
          </div>
          <DialogFooter className="flex-row justify-end gap-2">
            <Button variant="outline" onClick={() => setSel(null)} data-testid="giftcard-cancel">Annulla</Button>
            <Button onClick={pay} disabled={paying} className="gap-1" data-testid="giftcard-pay">
              {paying ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />} Paga {amount ? eur(parseFloat(amount)) : ""}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
